/**
 * Internationalization Configuration
 *
 * This module manages the i18n configuration and language settings
 * based on environment configuration and user preferences.
 */

/**
 * Supported languages in the application
 */
export const SUPPORTED_LANGUAGES = ['en', 'zh'] as const

export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number]

/**
 * Language display names for UI
 */
export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: 'English',
  zh: '中文 (简体)',
}

/**
 * Validates if a language code is supported
 */
export const isSupportedLanguage = (lang: string): lang is SupportedLanguage => {
  return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)
}

/**
 * Gets the default language from environment or fallback
 */
export const getDefaultLanguage = (): SupportedLanguage => {
  const envLanguage = import.meta.env.VITE_DEFAULT_LANGUAGE

  if (envLanguage && typeof envLanguage === 'string') {
    const trimmedLanguage = envLanguage.trim().toLowerCase()
    
    if (isSupportedLanguage(trimmedLanguage)) {
      return trimmedLanguage
    }
    
    console.warn(
      `VITE_DEFAULT_LANGUAGE "${envLanguage}" is not supported. Falling back to 'en'. ` +
      `Supported languages: ${SUPPORTED_LANGUAGES.join(', ')}`
    )
  }

  return 'zh'
}

/**
 * Gets the browser's preferred language if supported
 */
export const getBrowserLanguage = (): SupportedLanguage => {
  const browserLang = navigator.language.toLowerCase()
  
  // Check exact match first
  if (isSupportedLanguage(browserLang)) {
    return browserLang
  }
  
  // Check language prefix (e.g., 'zh-CN' -> 'zh')
  const langPrefix = browserLang.split('-')[0]
  if (isSupportedLanguage(langPrefix)) {
    return langPrefix
  }
  
  return getDefaultLanguage()
}

/**
 * Default language configuration
 * This is evaluated once at module load time
 */
export const defaultLanguage: SupportedLanguage = getDefaultLanguage()

/**
 * Browser detected language
 */
export const browserLanguage: SupportedLanguage = getBrowserLanguage()

if (import.meta.env.DEV) {
  console.info(`i18n: Default language set to '${defaultLanguage}'`)
  console.info(`i18n: Browser language detected as '${browserLanguage}'`)
}
